import { Check, ListPlus, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { api } from "../api";
import type { InventoryItem } from "../types";

export function ContentsEditor({
  unitId,
  items,
  onChanged,
  onClose
}: {
  unitId: string;
  items: InventoryItem[];
  onChanged: () => void;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<"rapid" | "detailed">("rapid");
  const [text, setText] = useState("");
  const [preview, setPreview] = useState<
    Array<{ source_line: string; item_name: string; quantity: string; unit_of_measure: string; confidence: string }>
  >([]);
  const [form, setForm] = useState({ item_name: "", quantity: "1", unit_of_measure: "each", notes: "" });
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");

  async function interpret() {
    setWorking(true);
    setError("");
    try {
      setPreview(await api.rapidPreview(unitId, text));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not interpret list");
    } finally {
      setWorking(false);
    }
  }

  async function saveRapid() {
    setWorking(true);
    try {
      await api.rapidSave(unitId, preview);
      setText("");
      setPreview([]);
      onChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not save contents");
    } finally {
      setWorking(false);
    }
  }

  async function saveDetailed(addAnother = false) {
    setWorking(true);
    try {
      await api.addItem(unitId, { ...form, quantity: Number(form.quantity) });
      setForm({ item_name: "", quantity: "1", unit_of_measure: "each", notes: "" });
      onChanged();
      if (!addAnother) onClose();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not add item");
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal-sheet" role="dialog" aria-modal="true" aria-labelledby="contents-title">
        <header>
          <div>
            <h2 id="contents-title">Inventory contents</h2>
            <p>{items.length} item lines currently recorded</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close">
            <X />
          </button>
        </header>
        <div className="segmented">
          <button className={mode === "rapid" ? "active" : ""} onClick={() => setMode("rapid")}>
            <ListPlus size={18} /> Rapid entry
          </button>
          <button className={mode === "detailed" ? "active" : ""} onClick={() => setMode("detailed")}>
            <Plus size={18} /> Detailed entry
          </button>
        </div>
        {mode === "rapid" ? (
          <div className="rapid-entry">
            <label>
              One item per line
              <textarea
                rows={7}
                value={text}
                onChange={(event) => setText(event.target.value)}
                placeholder={"Extension cords x4\nSoldering station\nUSB cables, approximately 20"}
              />
            </label>
            {!preview.length ? (
              <button className="button primary full" onClick={interpret} disabled={!text.trim() || working}>
                Interpret list
              </button>
            ) : (
              <>
                <p className="preview-note">Review the interpreted quantities before saving.</p>
                <div className="rapid-preview">
                  {preview.map((item, index) => (
                    <div key={`${item.source_line}-${index}`}>
                      <input
                        aria-label={`Item ${index + 1} name`}
                        value={item.item_name}
                        onChange={(event) =>
                          setPreview(
                            preview.map((row, rowIndex) =>
                              rowIndex === index ? { ...row, item_name: event.target.value } : row
                            )
                          )
                        }
                      />
                      <input
                        aria-label={`Item ${index + 1} quantity`}
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={item.quantity}
                        onChange={(event) =>
                          setPreview(
                            preview.map((row, rowIndex) =>
                              rowIndex === index ? { ...row, quantity: event.target.value } : row
                            )
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
                <button className="button primary full" onClick={saveRapid} disabled={working}>
                  <Check size={19} /> Save {preview.length} items
                </button>
              </>
            )}
          </div>
        ) : (
          <form
            className="editor-form compact"
            onSubmit={(event) => {
              event.preventDefault();
              saveDetailed(false);
            }}
          >
            <label>
              Item name
              <input
                value={form.item_name}
                onChange={(event) => setForm({ ...form, item_name: event.target.value })}
                required
                autoFocus
              />
            </label>
            <div className="form-grid two">
              <label>
                Quantity
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.quantity}
                  onChange={(event) => setForm({ ...form, quantity: event.target.value })}
                />
              </label>
              <label>
                Unit
                <input
                  value={form.unit_of_measure}
                  onChange={(event) => setForm({ ...form, unit_of_measure: event.target.value })}
                />
              </label>
            </div>
            <label>
              Notes
              <textarea
                value={form.notes}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
              />
            </label>
            <div className="form-actions">
              <button className="button secondary" type="button" onClick={() => saveDetailed(true)}>
                Add another item
              </button>
              <button className="button primary" disabled={working}>
                Save item
              </button>
            </div>
          </form>
        )}
        {error && <p className="form-error">{error}</p>}
        {items.length > 0 && (
          <details className="existing-items">
            <summary>View existing items</summary>
            {items.map((item) => (
              <div key={item.id}>
                <span>
                  {item.item_name} <small>× {item.quantity}</small>
                </span>
                <Trash2 size={17} aria-label="Remove from the main record screen" />
              </div>
            ))}
          </details>
        )}
      </section>
    </div>
  );
}
